/**
 * Minor units in, strings out. Every price in the control plane is an integer
 * number of pence/cents; the only place a decimal exists is on screen.
 */
const SYMBOL: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };

export function symbolFor(currency: string): string {
  return SYMBOL[currency?.toUpperCase()] ?? `${currency} `;
}

/** '£19.99', '£3', '—' for nothing set. Whole amounts drop the fraction. */
export function money(minor: number | null | undefined, currency: string): string {
  if (minor == null || !Number.isFinite(minor)) return '—';
  const symbol = symbolFor(currency);
  return minor % 100 === 0 ? `${symbol}${minor / 100}` : `${symbol}${(minor / 100).toFixed(2)}`;
}

/** The editable form of a price: major units, no symbol, blank for null. */
export function majorInput(minor: number | null | undefined): string {
  if (minor == null || !Number.isFinite(minor)) return '';
  return (minor / 100).toFixed(2).replace(/\.00$/, '');
}

/**
 * '19.99' -> 1999. Blank is null (a price that is not set, which is different
 * from zero). Anything that is not a number is undefined, so a caller can tell
 * a typo from a deliberate clearing.
 */
export function parseMajor(input: string): number | null | undefined {
  const raw = input.trim();
  if (raw === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 100);
}

export function parseCount(input: string): number | null | undefined {
  const raw = input.trim();
  if (raw === '') return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0) return undefined;
  return value;
}

export function pct(value: number | string | null | undefined): string {
  if (value == null) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}

export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export function onlyDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}
